import axios from 'axios'
import { getIdToken } from './authUtils'
import { getEnvironment } from './awsUtils'
import { blobToBase64 } from './fileUtils'
import { logError, logInfo, logWarn } from './logUtils'
import { fetchS3File } from './s3Utils'
import { getCloudFrontUrl, getIdTokenFromStorage, getNewMediaCloudfrontUrl } from './storageUtils'

export const mediaTypes = {
  savedFile: 'savedFile',
  scanSummary: 'scanSummary',
  referralAttachments: 'referralAttachments',
  noteAttachments: 'noteAttachments',
  bulkInvites: 'bulkInvites',
  bulkCreatePatients: 'bulkCreatePatients'
}

export const generateMediaPath = async ({ type, objectIdToAuthorize, keySuffix }) => {
  let objectType, isPrivate

  switch (type) {
    case 'scanReview':
      objectType = 'scanReviews'
      isPrivate = true
      break
    case mediaTypes.scanSummary:
      objectType = 'scanSummaries'
      isPrivate = true
      break
    case mediaTypes.savedFile:
      objectType = 'savedFiles'
      isPrivate = true
      break
    case mediaTypes.referralAttachments:
      objectType = 'referralAttachments'
      isPrivate = true
      break
    case mediaTypes.noteAttachments:
      objectType = 'noteAttachments'
      isPrivate = true
      break
    case mediaTypes.bulkInvites:
      objectType = 'bulkInvites'
      isPrivate = true
      break
    case mediaTypes.bulkCreatePatients:
      objectType = 'bulkCreatePatients'
      isPrivate = true
      break
    default:
      break
  }

  const idToken = await getIdToken()

  return `${isPrivate ? 'private' : 'public'}/${objectType}/${
    isPrivate ? `${objectIdToAuthorize}/` : ''
  }${keySuffix}?idToken=${idToken}`
}

export const putObject = async ({ path, data, contentType, headers = {} }) => {
  const response = await fetch(`${getNewMediaCloudfrontUrl()}/${path}`, {
    method: 'PUT',
    headers: {
      Accept: 'application/json',
      'Content-Type': contentType,
      ...headers
    },
    body: data
  })

  if (!response.ok) {
    logWarn(`putObject: failed to upload media to path: '${path}'`, {
      responseStatus: response.status,
      response: await response.json(),
      contentType
    })
    throw response
  }
}

export const generateMediaAndUpload = async ({ type, objectIdToAuthorize, keySuffix, data, contentType }) => {
  const path = await generateMediaPath({
    type,
    objectIdToAuthorize,
    keySuffix
  })
  await putObject({
    path,
    data,
    contentType
  })
}

export const getObjectUrl = s3Object =>
  !s3Object
    ? null
    : s3Object?.bucket?.includes('scans')
    ? `${getCloudFrontUrl()}/${
        s3Object.key.includes('scanSummaries') ? s3Object.key.replace('public/', '') : s3Object.key
      }`
    : `${getNewMediaCloudfrontUrl()}/${s3Object.key}?idToken=${getIdTokenFromStorage()}`

export const getObjectForDownload = async url => {
  try {
    logInfo(`New Media: downloading object`, { url })
    const file = await axios.get(url, { responseType: 'blob' })
    return file.data
  } catch (err) {
    // If unauthorized, try to refresh idToken and retry once
    const status = err?.response?.status
    if (status === 401) {
      try {
        const freshToken = await getIdToken()
        if (freshToken) {
          const newUrl = url.includes('idToken=')
            ? url.replace(/idToken=[^&]+/, `idToken=${freshToken}`)
            : `${url}${url.includes('?') ? '&' : '?'}idToken=${freshToken}`
          logInfo('New Media: retrying download with refreshed token', { newUrl })
          const retryFile = await axios.get(newUrl, { responseType: 'blob' })
          return retryFile.data
        }
      } catch (retryErr) {
        console.error('New Media: retry after 401 failed', {
          url,
          message: retryErr?.message,
          status: retryErr?.response?.status
        })
      }
    }
    console.error('An error occured while trying to get object from download', {
      url,
      message: err?.message,
      status
    })
  }
}

export const isNewMediaFile = s3Object => !!(s3Object?.key?.includes('private') || s3Object?.bucket?.includes('media'))

export const downloadMedia = file =>
  isNewMediaFile(file) ? getObjectForDownload(getObjectUrl(file)) : fetchS3File(file)

export const loadImage = async imageData => {
  let finalUrl
  let objectUrlToRevoke = null

  // Handle Blob inputs (from axios/fetch with responseType 'blob')
  if (imageData instanceof Blob) {
    if (imageData.type && imageData.type.startsWith('image/')) {
      // Safe object URL for image blobs
      finalUrl = URL.createObjectURL(imageData)
      objectUrlToRevoke = finalUrl
    } else {
      // Fallback: convert non-image blobs (e.g., octet-stream) to base64 data URL
      finalUrl = await blobToBase64(imageData)
    }
  } else if (typeof imageData === 'string') {
    // Handle URLs and data URIs
    if (imageData.startsWith('http')) {
      // For private/new-media URLs that often include idToken or reside under /private/, fetch as blob with retry
      if (imageData.includes('idToken=') || imageData.includes('/private/')) {
        try {
          const blob = await getObjectForDownload(imageData)
          if (blob instanceof Blob) {
            finalUrl = URL.createObjectURL(blob)
            objectUrlToRevoke = finalUrl
          } else if (typeof blob === 'string') {
            // Old media path returns base64 string (without prefix)
            finalUrl = `data:image/png;base64,${blob}`
          }
        } catch (e) {
          // Fall back to direct URL if fetching fails
          finalUrl = imageData
        }
      }
      // If not private or fetch path failed to set finalUrl, use the URL directly
      finalUrl = finalUrl || imageData
    } else if (imageData.startsWith('data:') || imageData.startsWith('blob:')) {
      finalUrl = imageData
    } else {
      // Assume raw base64 without data URL prefix
      finalUrl = `data:image/png;base64,${imageData}`
    }
  } else {
    // Unknown type; log and reject
    return Promise.reject(logError('loadImage: unsupported imageData type', { imageData }))
  }

  return new Promise((resolve, reject) => {
    const img = new Image()
    if (typeof finalUrl === 'string' && finalUrl.startsWith('http')) {
      img.crossOrigin = 'anonymous'
    }
    img.onload = () => {
      if (objectUrlToRevoke) {
        try {
          URL.revokeObjectURL(objectUrlToRevoke)
        } catch (_) {}
      }
      resolve(img)
    }
    img.onerror = error => {
      if (objectUrlToRevoke) {
        try {
          URL.revokeObjectURL(objectUrlToRevoke)
        } catch (_) {}
      }
      reject(logError('An error occured while loading image', { error, imageData }))
    }
    img.src = finalUrl
  })
}

export const getImageDataURI = ({ imageUrl, useAmplifyStorage = false }) => {
  const fetchImageData = url => {
    return fetch(url)
      .then(response => {
        if (!response.ok) {
          throw new Error(`Failed to fetch image: ${response.status} - ${response.statusText}`)
        }
        return response.blob()
      })
      .then(blob => {
        const reader = new FileReader()
        return new Promise(resolve => {
          reader.onloadend = () => {
            const dataUriResult = reader.result.replace('data:binary/octet-stream', 'data:image/png')
            resolve(dataUriResult)
          }
          reader.readAsDataURL(blob)
        })
      })
  }

  return new Promise((resolve, reject) => {
    if (useAmplifyStorage) {
      Storage.get(imageUrl).then(fetchImageData).then(resolve).catch(reject)
    } else {
      fetchImageData(imageUrl).then(resolve).catch(reject)
    }
  })
}

export const getNewMediaBucket = () => `media-bucket-${getEnvironment()}`
export const extractScanIdFromScanSummaryKey = scanSummaryKey => scanSummaryKey?.split?.('/')?.[2] || null
